import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useCourses } from '../hooks/useWebsiteContent';
import { mockData } from '../data/mock';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { ShoppingCart, Check, Info, Clock, Users, BookOpen, X, Loader2 } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useToast } from '../hooks/use-toast';

const CoursesSection = () => {
  const { language } = useLanguage();
  const { addToCart } = useCart();
  const { toast } = useToast();
  const { courses, loading } = useCourses();
  const [selectedCourse, setSelectedCourse] = useState(null);

  const content = {
    en: {
      title: "Our English Courses",
      subtitle: "Choose the perfect course for your learning journey",
      addToCart: "Add to Cart",
      added: "Added!",
      off: "off",
      viewDetails: "View Details",
      features: "Course Features",
      close: "Close",
      priceNote: "Note"
    },
    ar: {
      title: "دوراتنا الإنجليزية",
      subtitle: "اختر الدورة المثالية لرحلة التعلم الخاصة بك",
      addToCart: "أضف للسلة",
      added: "تمت الإضافة!",
      off: "خصم",
      viewDetails: "التفاصيل",
      features: "مميزات الدورة",
      close: "إغلاق",
      priceNote: "ملاحظة"
    }
  };

  const t = content[language];

  const handleAddToCart = (course) => {
    addToCart(course);
    toast({
      title: language === 'en' ? '✓ Added to Cart!' : '✓ تمت الإضافة للسلة!',
      description: course.title[language],
      className: "bg-teal-600 text-white"
    });
    setSelectedCourse(null);
  };

  return (
    <section id="courses" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">{t.title}</h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">{t.subtitle}</p>
        </div>

        {/* Courses Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {loading ? (
            <div className="col-span-3 flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
            </div>
          ) : (
          courses.map((course) => (
            <div
              key={course.id}
              className="group bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-500 border border-gray-100"
            >
              {/* Course Image */}
              <div className="relative h-48 overflow-hidden">
                <img
                  src={course.image}
                  alt={`${course.title[language]} - English course at Canada English Life Academy`}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                {/* Discount Badge */}
                <Badge className="absolute top-4 right-4 bg-orange-500 hover:bg-orange-600 text-white text-sm font-bold px-3 py-1">
                  {course.discount}% {t.off}
                </Badge>
              </div>

              {/* Course Info */}
              <div className="p-5">
                <h3 className="text-lg font-bold text-gray-900 mb-2 min-h-[48px] line-clamp-2">
                  {course.title[language]}
                </h3>

                {/* Short Description */}
                {course.description && (
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                    {course.description[language]}
                  </p>
                )}

                {/* Pricing */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-baseline gap-2">
                    <span className="text-2xl font-bold text-teal-600">
                      {course.price.current}
                    </span>
                    <span className="text-sm text-gray-500">{course.price.currency[language]}</span>
                  </div>
                  <span className="text-sm text-gray-400 line-through">
                    {course.price.original} {course.price.currency[language]}
                  </span>
                </div>

                {/* Buttons */}
                <div className="flex gap-2">
                  <Button 
                    onClick={() => setSelectedCourse(course)}
                    variant="outline"
                    className="flex-1"
                  >
                    <Info className={`w-4 h-4 ${language === 'ar' ? 'ml-2' : 'mr-2'}`} />
                    {t.viewDetails}
                  </Button>
                  <Button 
                    onClick={() => handleAddToCart(course)}
                    data-testid={`add-to-cart-btn-${course.id}`}
                    className="flex-1 bg-teal-600 hover:bg-teal-700 text-white"
                  >
                    <ShoppingCart className={`w-4 h-4 ${language === 'ar' ? 'ml-2' : 'mr-2'}`} />
                    {t.addToCart}
                  </Button>
                </div>
              </div>
            </div>
          ))
          )}
        </div>

        {/* Course Categories */}
        <div className="mt-20">
          <h3 className="text-3xl font-bold text-center text-gray-900 mb-12">
            {language === 'en' ? 'Explore Categories' : 'استكشف الفئات'}
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {mockData.categories.map((category) => (
              <div
                key={category.id}
                className="relative rounded-xl overflow-hidden h-48 group cursor-pointer"
              >
                <img
                  src={category.image}
                  alt={category.title[language]}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end p-4">
                  <h4 className="text-white font-bold text-lg">{category.title[language]}</h4>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Course Details Modal */}
      <Dialog open={!!selectedCourse} onOpenChange={() => setSelectedCourse(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedCourse && (
            <>
              <DialogHeader>
                <DialogTitle className="text-2xl font-bold text-gray-900 pr-8">
                  {selectedCourse.title[language]}
                </DialogTitle>
              </DialogHeader>

              {/* Course Image */}
              <div className="relative h-48 rounded-xl overflow-hidden mt-4">
                <img
                  src={selectedCourse.image}
                  alt={selectedCourse.title[language]}
                  className="w-full h-full object-cover"
                />
                <Badge className="absolute top-4 right-4 bg-orange-500 text-white text-sm font-bold px-3 py-1">
                  {selectedCourse.discount}% {t.off}
                </Badge>
              </div>

              {/* Description */}
              {selectedCourse.description && (
                <div className="mt-6">
                  <p className="text-gray-700 leading-relaxed">
                    {selectedCourse.description[language]}
                  </p>
                </div>
              )}

              {/* Features */}
              {selectedCourse.features && (
                <div className="mt-6">
                  <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                    <BookOpen className={`w-5 h-5 text-teal-600 ${language === 'ar' ? 'ml-2' : 'mr-2'}`} />
                    {t.features}
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {selectedCourse.features[language].map((feature, index) => (
                      <div 
                        key={index}
                        className="flex items-center bg-teal-50 rounded-lg p-3"
                      >
                        <Check className={`w-5 h-5 text-teal-600 flex-shrink-0 ${language === 'ar' ? 'ml-3' : 'mr-3'}`} />
                        <span className="text-gray-700 text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Pricing Section */}
              <div className="mt-6 bg-gradient-to-r from-teal-50 to-emerald-50 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <span className="text-sm text-gray-500 block mb-1">
                      {language === 'en' ? 'Price' : 'السعر'}
                    </span>
                    <div className="flex items-baseline gap-3">
                      <span className="text-4xl font-bold text-teal-600">
                        {selectedCourse.price.current}
                      </span>
                      <span className="text-lg text-gray-500">
                        {selectedCourse.price.currency[language]}
                      </span>
                      <span className="text-lg text-gray-400 line-through">
                        {selectedCourse.price.original} {selectedCourse.price.currency[language]}
                      </span>
                    </div>
                  </div>
                  <Badge className="bg-orange-500 text-white text-lg px-4 py-2">
                    {language === 'en' ? 'Save' : 'وفر'} {selectedCourse.discount}%
                  </Badge>
                </div>

                {/* Price Note */}
                {selectedCourse.priceNote && (
                  <p className="text-sm text-gray-600 bg-white rounded-lg p-3 mb-4">
                    <strong>{t.priceNote}:</strong> {selectedCourse.priceNote[language]}
                  </p>
                )}

                {/* Add to Cart Button */}
                <Button 
                  onClick={() => handleAddToCart(selectedCourse)}
                  className="w-full bg-teal-600 hover:bg-teal-700 text-white text-lg py-6"
                >
                  <ShoppingCart className={`w-5 h-5 ${language === 'ar' ? 'ml-2' : 'mr-2'}`} />
                  {t.addToCart}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default CoursesSection;
